/**
 * Vehicle Sighting Form Component
 * Modal form for reporting vehicle sightings with plate, type, color, direction
 */

import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import {
  VehicleSighting,
  VehicleType,
  VehicleDirection,
  PlateConfidence,
  SightingPhoto,
  VEHICLE_TYPES,
  VEHICLE_COLORS,
  normalizePlate,
  isValidPlate,
  MAX_DESCRIPTION_LENGTH,
  DEFAULT_VEHICLE_SIGHTING_EXPIRATION_MINUTES,
} from '../../lib/vehicles/types'
import type { GeoLocation } from '../../lib/types'
import { PhotoCapture, type CapturedPhoto } from './PhotoCapture'
import { v4 as uuidv4 } from 'uuid'

interface SightingFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (sighting: VehicleSighting) => void
  location: GeoLocation
  senderId: string
  senderName: string
}

export function SightingForm({
  isOpen,
  onClose,
  onSubmit,
  location,
  senderId,
  senderName,
}: SightingFormProps) {
  const { t } = useTranslation()

  const [plate, setPlate] = useState('')
  const [plateConfidence, setPlateConfidence] = useState<PlateConfidence>('full')
  const [vehicleType, setVehicleType] = useState<VehicleType | undefined>('suv')
  const [vehicleColor, setVehicleColor] = useState<string | undefined>('black')
  const [direction, setDirection] = useState<VehicleDirection | undefined>(undefined)
  const [description, setDescription] = useState('')
  const [isFollowing, setIsFollowing] = useState(false)
  const [photos, setPhotos] = useState<CapturedPhoto[]>([])

  // Handle photo capture
  const handlePhotoCapture = useCallback((photo: CapturedPhoto) => {
    setPhotos(prev => [...prev, photo])
  }, [])

  // Handle OCR-detected plate from photo
  const handlePlateDetected = useCallback((detectedPlate: string, confidence: number) => {
    // Only auto-fill if plate field is empty
    if (!plate.trim() && detectedPlate) {
      setPlate(detectedPlate)
      // Set confidence based on OCR confidence
      if (confidence > 0.8) {
        setPlateConfidence('full')
      } else if (confidence > 0.5) {
        setPlateConfidence('partial')
      }
    }
  }, [plate])

  const handleSubmit = useCallback(() => {
    const now = new Date()
    const expiresAt = new Date(now.getTime() + DEFAULT_VEHICLE_SIGHTING_EXPIRATION_MINUTES * 60 * 1000)

    // Convert captured photos to SightingPhoto format
    const sightingPhotos: SightingPhoto[] = photos.map(photo => ({
      id: photo.id,
      thumbnailDataUrl: photo.thumbnail,
      blobKey: photo.id, // Key for IndexedDB storage
      location: photo.location,
      timestamp: photo.timestamp,
      plateOCR: photo.plateResult ? {
        text: photo.plateResult.text,
        confidence: photo.plateResult.confidence,
      } : undefined,
    }))

    const sighting: VehicleSighting = {
      id: uuidv4(),
      type: 'vehicle_sighting',
      location,
      plate: plate.trim() ? normalizePlate(plate) : undefined,
      plateConfidence: plate.trim() ? plateConfidence : 'none',
      vehicleType,
      vehicleColor,
      description: description.trim() || undefined,
      direction,
      isFollowing,
      followerId: isFollowing ? senderId : undefined,
      photos: sightingPhotos.length > 0 ? sightingPhotos : undefined,
      senderId,
      senderName,
      createdAt: now,
      expiresAt,
    }

    onSubmit(sighting)

    // Reset form
    setPlate('')
    setPlateConfidence('full')
    setVehicleType('suv')
    setVehicleColor('black')
    setDirection(undefined)
    setDescription('')
    setIsFollowing(false)
    setPhotos([])
    onClose()
  }, [
    plate, plateConfidence, vehicleType, vehicleColor, direction,
    description, isFollowing, photos, location, senderId, senderName, onSubmit, onClose
  ])

  const handlePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().slice(0, 10)
    setPlate(value)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md bg-[#271b1b] rounded-xl shadow-hard border border-white/10 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <span>🚐</span>
            {t('vehicle.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white transition-colors"
            aria-label={t('accessibility.close')}
          >
            ✕
          </button>
        </div>

        {/* Form Content */}
        <div className="p-4 space-y-4">
          {/* Photo Capture */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              {t('vehicle.attachPhoto')} <span className="text-white/50">{t('vehicle.photoOptional')}</span>
            </label>
            <PhotoCapture
              onPhotoCapture={handlePhotoCapture}
              onPlateDetected={handlePlateDetected}
              currentLocation={location}
              maxPhotos={3}
            />
          </div>

          {/* License Plate */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">
              {t('vehicle.plate')} <span className="text-white/50">{t('vehicle.plateOptional')}</span>
            </label>
            <input
              type="text"
              value={plate}
              onChange={handlePlateChange}
              placeholder="ABC-1234"
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg
                text-white placeholder-white/40 focus:outline-none focus:border-primary
                uppercase tracking-wider font-mono text-lg"
            />
            {plate && !isValidPlate(plate) && (
              <p className="text-xs text-red-400 mt-1">Plate must be 2-10 characters</p>
            )}
          </div>

          {/* Plate Confidence */}
          {plate.trim() && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                {t('vehicle.plateConfidence')}
              </label>
              <div className="flex gap-2">
                {(['full', 'partial', 'none'] as PlateConfidence[]).map((conf) => (
                  <button
                    key={conf}
                    type="button"
                    onClick={() => setPlateConfidence(conf)}
                    className={clsx(
                      'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      plateConfidence === conf
                        ? 'bg-primary text-white'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    )}
                  >
                    {t(`vehicle.plate${conf.charAt(0).toUpperCase() + conf.slice(1)}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Vehicle Type & Color */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                {t('vehicle.type')}
              </label>
              <select
                value={vehicleType || ''}
                onChange={(e) => setVehicleType(e.target.value as VehicleType || undefined)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg
                  text-white focus:outline-none focus:border-primary"
              >
                <option value="">--</option>
                {VEHICLE_TYPES.map((type) => (
                  <option key={type.key} value={type.key}>
                    {type.emoji} {t(`vehicle.type${type.key.charAt(0).toUpperCase() + type.key.slice(1)}`)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                {t('vehicle.color')}
              </label>
              <select
                value={vehicleColor || ''}
                onChange={(e) => setVehicleColor(e.target.value || undefined)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg
                  text-white focus:outline-none focus:border-primary"
              >
                <option value="">--</option>
                {VEHICLE_COLORS.map((color) => (
                  <option key={color.key} value={color.key}>
                    {t(`vehicle.color${color.key.charAt(0).toUpperCase() + color.key.slice(1)}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Direction */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              {t('vehicle.direction')}
            </label>
            <div className="grid grid-cols-5 gap-1">
              {/* Top row: NW N NE */}
              <button
                type="button"
                onClick={() => setDirection('NW')}
                className={clsx(
                  'p-2 rounded text-xl transition-colors',
                  direction === 'NW' ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
                )}
              >↖</button>
              <button
                type="button"
                onClick={() => setDirection('N')}
                className={clsx(
                  'p-2 rounded text-xl transition-colors',
                  direction === 'N' ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
                )}
              >↑</button>
              <button
                type="button"
                onClick={() => setDirection('NE')}
                className={clsx(
                  'p-2 rounded text-xl transition-colors',
                  direction === 'NE' ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
                )}
              >↗</button>
              <div />
              <button
                type="button"
                onClick={() => setDirection('stationary')}
                className={clsx(
                  'p-2 rounded text-xl transition-colors',
                  direction === 'stationary' ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
                )}
              >⊙</button>

              {/* Middle row: W _ E */}
              <button
                type="button"
                onClick={() => setDirection('W')}
                className={clsx(
                  'p-2 rounded text-xl transition-colors',
                  direction === 'W' ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
                )}
              >←</button>
              <div />
              <button
                type="button"
                onClick={() => setDirection('E')}
                className={clsx(
                  'p-2 rounded text-xl transition-colors',
                  direction === 'E' ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
                )}
              >→</button>
              <div />
              <div />

              {/* Bottom row: SW S SE */}
              <button
                type="button"
                onClick={() => setDirection('SW')}
                className={clsx(
                  'p-2 rounded text-xl transition-colors',
                  direction === 'SW' ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
                )}
              >↙</button>
              <button
                type="button"
                onClick={() => setDirection('S')}
                className={clsx(
                  'p-2 rounded text-xl transition-colors',
                  direction === 'S' ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
                )}
              >↓</button>
              <button
                type="button"
                onClick={() => setDirection('SE')}
                className={clsx(
                  'p-2 rounded text-xl transition-colors',
                  direction === 'SE' ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
                )}
              >↘</button>
              <div />
              <div />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">
              {t('vehicle.description')}
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
              placeholder={t('vehicle.descriptionPlaceholder')}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg
                text-white placeholder-white/40 focus:outline-none focus:border-primary"
            />
            <p className="text-xs text-white/40 mt-1 text-right">
              {description.length}/{MAX_DESCRIPTION_LENGTH}
            </p>
          </div>

          {/* Following Toggle */}
          <label className="flex items-center gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
            <input
              type="checkbox"
              checked={isFollowing}
              onChange={(e) => setIsFollowing(e.target.checked)}
              className="w-5 h-5 rounded border-white/20 bg-white/10 text-primary
                focus:ring-primary focus:ring-offset-0"
            />
            <span className="text-white/90 font-medium">{t('vehicle.following')}</span>
          </label>
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-3 p-4 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-white/10 text-white/80 rounded-lg font-medium
              hover:bg-white/20 transition-colors"
          >
            {t('vehicle.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 px-4 py-3 bg-[#92400e] text-white rounded-lg font-bold
              hover:bg-[#a3510f] transition-colors shadow-hard-sm flex items-center justify-center gap-2"
          >
            <span>🚐</span>
            {t('vehicle.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default SightingForm
